/** Read or Write, as a labelled segmented control.
 *
 * This was a 16px pen glyph sitting between a publish toggle and a fullscreen
 * button. A MODE does not belong in a row of actions: it changes what every
 * later click means, so it has to be the most legible thing in the cluster and
 * it has to say which state you are in without a tooltip. Two words do that; an
 * icon with `aria-pressed` does not.
 *
 * It lives in its own module because three surfaces render it — the reader's
 * doc-controls island, the reader's fullscreen island, and the annotation
 * toolbar's trailing group — and a control whose two halves mean "everything
 * you click next is a stroke" / "…is a selection" is the last thing that should
 * exist in three copies free to drift.
 */
import { Eye, PenLine } from "lucide-react";

import { cn } from "@/lib/utils";
import { useAnnotationTool } from "@/stores/annotation-tool";

export function ModeSwitch({ on, className }: { on: boolean; className?: string }) {
  const set = (write: boolean) => {
    const s = useAnnotationTool.getState();
    s.setTool(write ? s.lastTool : "none");
  };
  return (
    <div
      role="group"
      aria-label="Reading or writing"
      className={cn("flex items-center gap-0.5 rounded-md bg-secondary/70 p-0.5", className)}
    >
      <button
        type="button"
        aria-pressed={!on}
        aria-label="Read"
        onClick={() => set(false)}
        className={cn(
          "flex h-[26px] items-center gap-1.5 rounded px-2 text-[12px] font-medium transition-colors",
          on
            ? "text-muted-foreground hover:text-foreground"
            : "bg-background text-foreground shadow-sm",
        )}
      >
        <Eye className="h-3.5 w-3.5" />
        Read
      </button>
      <button
        type="button"
        aria-pressed={on}
        aria-label="Write"
        onClick={() => set(true)}
        className={cn(
          "flex h-[26px] items-center gap-1.5 rounded px-2 text-[12px] font-medium transition-colors",
          on
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <PenLine className="h-3.5 w-3.5" />
        Write
      </button>
    </div>
  );
}
